import { forwardRef, memo, useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { ErrorMessage, RefreshErrorTypes } from '@/modules/common';
import { MessageSenderType, MessageType, SendToModelStatusType } from '@/modules/models';
import {
  bridgeAtom,
  chatFeedbackAtom,
  errorAtom,
  isLoadingMessageAtom,
  messagesAtom,
  renderedMessagesAtom,
  useAtom,
  useValue,
} from '@/store';
import { isHebrew } from '@/utils';
import { BubbleLoading, MessageText, MultiChoice, SingleChoice } from './components';
import { ChoiceType } from './data';
import {
  AgentAvatar,
  AgentNameTag,
  LiveAgentContent,
  LiveAgentMessageRow,
  MessageContainer,
  MessagesContainer,
  RemoveMessageIcon,
  RemoveMessageIconWrapper,
  WarningDescription,
  WarningDescriptionIcon,
} from './Messages.styles';

export interface MessagesProps {
  resendMessage: (msg: MessageType) => void;
  sendMessage: (msg?: string) => void;
  removeMessage: (msg: MessageType) => void;
}

export const Messages = memo(
  forwardRef<HTMLUListElement, MessagesProps>((props, ref) => {
    const { resendMessage, sendMessage, removeMessage } = props;

    const [error, setError] = useAtom(errorAtom);
    const history = useValue(messagesAtom);
    const { translations } = useValue(bridgeAtom);
    const chatFeedback = useValue(chatFeedbackAtom);
    const isLoadingMessage = useValue(isLoadingMessageAtom);
    const renderedMessages = useValue(renderedMessagesAtom);
    const isShowRefreshButton = useMemo(() => RefreshErrorTypes.includes(error!), [error]);

    const messagesRef = useRef<HTMLUListElement | null>(null);
    useImperativeHandle(ref, () => messagesRef.current as HTMLUListElement);

    const onClickRemoveMessage = useCallback(
      (msg: MessageType) => (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        removeMessage(msg);
      },
      [removeMessage]
    );

    const onClickReload = useCallback(() => {
      setError(null);
    }, [setError]);

    // auto-scrolling effect
    const isAllRendered = Object.values(renderedMessages).every((isRendered) => isRendered);
    useLayoutEffect(() => {
      if (!messagesRef.current || !isAllRendered) {
        return;
      }
      const lastMlMessage = messagesRef.current.querySelector<HTMLLIElement>('#last-ml-message');
      if (!lastMlMessage) {
        messagesRef.current.scrollTo({
          top: messagesRef.current.scrollHeight,
          behavior: 'smooth',
        });
        return;
      }
      const containerRect = messagesRef.current?.getBoundingClientRect();
      const messageRect = lastMlMessage.getBoundingClientRect();
      const isLastMlMessageInView = messageRect.top >= containerRect.top && messageRect.bottom <= containerRect.bottom;
      if (isLastMlMessageInView) {
        return;
      }
      const OFFSET = 20;
      messagesRef.current?.scrollTo({
        top: messageRect.top - containerRect.top + messagesRef.current.scrollTop - OFFSET,
        behavior: 'instant',
      });
    }, [history.length, isAllRendered]);

    // remember to reverse the array back when rendering
    const previousNonUserMessages = useMemo(() => {
      const result: (MessageType | undefined)[] = [];
      let lastNonUserMessage: MessageType | undefined;

      history.forEach((msg) => {
        result.push(lastNonUserMessage);
        if (msg.role !== MessageSenderType.USER) {
          lastNonUserMessage = msg;
        }
      });

      return result;
    }, [history]);

    return (
      <MessagesContainer
        hasChatFeedback={Boolean(chatFeedback)}
        ref={messagesRef}
        data-testid="saywhatt-chat-window-message-list"
      >
        {history.map((msg, index) => {
          const previousNonUserMessage = previousNonUserMessages[index];

          const key = msg.replyMessageId
            ? `${msg.replyMessageId}${msg.id}`
            : (msg.clientCorrelationId ?? msg.id ?? `la_${msg.clientTimestamp}`);

          const hasFailed = msg.status === SendToModelStatusType.FAILED;

          const isLastMessage = index === 0; // because we reversed the array
          const isLiveAgentMessage = msg.role === MessageSenderType.LIVE_AGENT;
          const agentName = msg.agent_name?.trim();
          const agentInitial = agentName ? agentName.charAt(0).toUpperCase() : null;
          const hasAgentAvatar = isLiveAgentMessage && Boolean(agentName);
          const isContinuationOfSameAgent =
            previousNonUserMessage?.role === MessageSenderType.LIVE_AGENT &&
            previousNonUserMessage.agent_name?.trim() === agentName;
          const showAgentNameTag = hasAgentAvatar && !isContinuationOfSameAgent;

          const isShowChoice =
            isLastMessage && msg.role === MessageSenderType.ASSISTANT && (msg.options?.length ?? 0) > 0;

          let Choice: React.ComponentType<ChoiceType> | null = null;

          if (isShowChoice) {
            Choice = msg.multiChoice ? MultiChoice : SingleChoice;
          }

          const isUserMessage = msg.role === MessageSenderType.USER;
          const isSystemMessage = msg.role === MessageSenderType.SYSTEM;

          const failedMessageProps = hasFailed
            ? {
                hasFailed,
                role: 'button',
                ariaLabel: 'resend button',
                onClick: () => resendMessage(msg),
              }
            : {};

          const content = msg.contentNew || msg.content;

          if (!content && !msg.attachments?.length) {
            return null;
          }

          const isHebrewMsg = isHebrew(msg.content);
          let messageElId;
          if (index === 0) {
            messageElId = 'first-message';
          } else if (index === history.length - 1 && !isUserMessage) {
            messageElId = 'last-ml-message';
          }

          return (
            <MessageContainer
              key={key}
              isUserMessage={isUserMessage}
              isSystemMessage={isSystemMessage}
              data-testid="saywhatt-chat-window-message"
              id={messageElId}
              {...failedMessageProps}
            >
              {!Choice &&
                (hasAgentAvatar ? (
                  <LiveAgentMessageRow>
                    <AgentAvatar data-testid="saywhatt-chat-live-agent-avatar" aria-hidden="true">
                      {agentInitial}
                    </AgentAvatar>
                    <LiveAgentContent>
                      {showAgentNameTag && (
                        <AgentNameTag data-testid="saywhatt-chat-live-agent-name">Agent {agentName}</AgentNameTag>
                      )}
                      <MessageText
                        id={msg.id}
                        links={msg.links}
                        attachments={msg.attachments}
                        isUserMessage={isUserMessage}
                        isSystemMessage={isSystemMessage}
                        isLiveAgentMessage={isLiveAgentMessage}
                        showAgentNameTag={showAgentNameTag}
                        text={content}
                        mlBeProcessingTime={msg.mlBeProcessingTimeMs}
                        direction={isHebrewMsg ? 'rtl' : 'ltr'}
                      />
                    </LiveAgentContent>
                  </LiveAgentMessageRow>
                ) : (
                  <MessageText
                    id={msg.id}
                    links={msg.links}
                    attachments={msg.attachments}
                    isUserMessage={isUserMessage}
                    isSystemMessage={isSystemMessage}
                    isLiveAgentMessage={isLiveAgentMessage}
                    text={content}
                    mlBeProcessingTime={msg.mlBeProcessingTimeMs}
                    direction={isHebrewMsg ? 'rtl' : 'ltr'}
                  />
                ))}

              {Choice && <Choice disabled={isLoadingMessage} options={msg.options!} sendMessage={sendMessage} />}

              {hasFailed && (
                <>
                  <RemoveMessageIconWrapper
                    role="button"
                    aria-label="remove button"
                    onClick={onClickRemoveMessage(msg)}
                  >
                    <RemoveMessageIcon />
                  </RemoveMessageIconWrapper>

                  <WarningDescription role="button" aria-label="resend button">
                    <WarningDescriptionIcon />
                    {window.LLL(translations.failedMessage)}
                  </WarningDescription>
                </>
              )}
            </MessageContainer>
          );
        })}
        {isLoadingMessage && !error && <BubbleLoading />}
        {error && (
          <ErrorMessage
            isShowRefreshButton={isShowRefreshButton}
            message={
              error ? (window.LLL(translations.errors[error]) ?? window.LLL(translations.errors.default)) : undefined
            }
            onClick={onClickReload}
          />
        )}
      </MessagesContainer>
    );
  })
);

Messages.displayName = 'Messages';
